// Per-run site table over the filtered selection. Clicking a row flies the map
// to that segment; the caret expands the row to list the ACTUAL crashes within
// the buffer — each crash line flies the map to that crash.
import React, { useState } from 'react';

const fmtMp = (v) => (v === null || v === undefined ? '—' : v.toFixed(3));

// The "worst" column means something different per analysis
const worstLabel = (analysis) => ({
  SSD: 'Worst (mph)', PSD: 'Worst (mph)', GLR: 'Glare (min/yr)',
  FSA: 'Cross slope (%)', HCA: 'Score (%)', GRW: 'Embank. ht (ft)',
  ISD: 'Required ISD (ft)', ALL: 'Worst',
}[analysis] || 'Worst');

const BUFFER_MI = 0.1;

export default function SitesTable({ sites, analysis, onFocus, crashes }) {
  const [openKey, setOpenKey] = useState(null);
  if (!sites?.length) return <div className="safety-muted">No {analysis} deficiency runs in the current selection.</div>;
  const showAnalysis = analysis === 'ALL';

  const crashesFor = (s) => (crashes || []).filter(
    (c) => c.milepost >= s.mpStart - BUFFER_MI && c.milepost <= s.mpEnd + BUFFER_MI);

  return (
    <div className="safety-table-scroll">
      <table className="safety-table safety-table-clickable">
        <thead>
          <tr>
            <th />
            {showAnalysis && <th>Analysis</th>}
            <th>Milepost</th><th>Dir</th><th>Stations</th><th>{worstLabel(analysis)}</th>
            <th>Severity</th><th>Length (mi)</th>
            <th>Crashes ±0.1 mi</th><th>Non-animal</th><th>Fatal+Injury</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((s) => {
            const key = `${s.analysis || analysis}-${s.direction}-${s.id}`;
            const open = openKey === key;
            const siteCrashes = open ? crashesFor(s) : [];
            return (
              <React.Fragment key={key}>
                <tr
                  className={s.nonAnimalCount > 0 ? 'safety-row-hit' : ''}
                  onClick={() => onFocus && onFocus({
                    bounds: (s.coords || []).filter((p) => p[0] != null),
                    lat: s.coords?.[0]?.[0], lng: s.coords?.[0]?.[1],
                    key: `site-${key}-${Date.now()}`,
                  })}
                  title="Show this segment on the map"
                >
                  <td onClick={(e) => { e.stopPropagation(); setOpenKey(open ? null : key); }}
                    className="safety-expander"
                    title={s.crashCount ? 'List the crashes at this site' : 'No crashes here'}>
                    {s.crashCount ? (open ? '▾' : '▸') : ''}
                  </td>
                  {showAnalysis && <td>{s.analysis}</td>}
                  <td>{fmtMp(s.mpStart)}–{fmtMp(s.mpEnd)}</td>
                  <td>{s.direction === 'P' ? 'EB' : 'WB'}</td>
                  <td>{s.curveNumber != null ? `curve #${s.curveNumber}` : s.nPoints}</td>
                  <td>{s.worstValue}</td>
                  <td><span className={`safety-sev safety-sev-${s.worstSeverity}`}>{s.worstSeverity}</span></td>
                  <td>{s.lengthMi}</td>
                  <td>{s.crashCount || '·'}</td>
                  <td>{s.nonAnimalCount ? <strong>{s.nonAnimalCount}</strong> : '·'}</td>
                  <td>{s.injuryCount || '·'}</td>
                </tr>
                {open && siteCrashes.length > 0 && (
                  <tr className="safety-site-crashes">
                    <td colSpan={showAnalysis ? 11 : 10}>
                      <ul>
                        {siteCrashes.map((c) => (
                          <li key={c.id}
                            onClick={() => onFocus && onFocus({
                              lat: c.lat, lng: c.lon, zoom: 16,
                              key: `sitecrash-${c.id}-${Date.now()}`,
                            })}
                            title="Show this crash on the map">
                            <span className={`safety-sev safety-sev-${c.severityClass === 'fatal' ? 'severe' : c.severityClass === 'injury' ? 'moderate' : 'minor'}`}>
                              {c.severityClass}
                            </span>
                            {' '}MP {fmtMp(c.milepost)} · {c.datetime ? c.datetime.slice(0, 10) : '—'} · {c.manner || 'crash'}
                            {c.surface && c.surface !== 'Dry' ? ` · ${c.surface}` : ''}
                            {c.isAnimal ? ' · animal' : ''}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
